import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Clock, MapPin, AlignLeft, Repeat, AlertTriangle, CalendarDays, Bell, Users } from 'lucide-react';
import { Sheet, Button, Field, Input, Textarea, Select, Switch, Banner } from '../kit';
import ParticipantPicker from '../workflow/ParticipantPicker';
import { addDays, ymd, parseYmd, fmtRange, fmtLongDate, clashesFor } from './calendarMath';
import { KINDS, kindOf, REPEATS, REMINDERS, COLORS, COLOR_KEYS } from './palette';
import { LinkPicker, AttendeesField, OrganizerSelect, useMyMailboxes } from './InviteFields';

const pad = (n) => String(n).padStart(2, '0');
const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const atTime = (date, time) => {
  const d = parseYmd(date);
  if (!d || !/^\d{2}:\d{2}$/.test(time || '')) return null;
  const [h, m] = time.split(':').map(Number);
  d.setHours(h, m, 0, 0);
  return d;
};
const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney'; } catch { return 'Australia/Sydney'; } })();

// The form's values from an appointment occurrence (editing) or a draft (new: { start, end, allDay }).
export function toForm(src, defaults = {}) {
  const allDay = src.all_day != null ? !!src.all_day : !!src.allDay;
  const start = src.start;
  const endShown = allDay ? addDays(src.end, -1) : src.end;
  return {
    title: src.id ? src.title || '' : (src.title || ''),
    kind: src.kind && src.kind !== 'keydate' ? src.kind : (defaults.kind || 'meeting'),
    coordination: src.coordination_id ? { id: src.coordination_id, name: src.participant || '' } : (defaults.coordination || null),
    coordinator_id: src.id ? (src.coordinator_id || '') : (src.coordinator_id || defaults.coordinator_id || ''),
    all_day: allDay,
    date: ymd(start), start_time: hm(start),
    end_date: ymd(endShown < start ? start : endShown), end_time: hm(src.end),
    repeat: src.repeat || 'none',
    repeat_until: src.repeat_until ? String(src.repeat_until).slice(0, 10) : '',
    location: src.location || '', notes: src.notes || '',
    color: src.color || '', status: src.status || 'scheduled',
    // A new appointment reminds its coordinator 30 minutes before (Google's default); an existing
    // one keeps what it has, including none.
    reminder: src.id ? (src.reminder_minutes == null ? '' : String(src.reminder_minutes)) : (defaults.reminder ?? '30'),
    // The Email calendar: what it is linked to, who is invited and from which mailbox.
    link: src.module === 'sc' && src.coordination_id ? { module: 'sc', id: src.coordination_id, client_id: src.client_id, name: src.participant || '' }
      : (src.module === 'pm' || src.module === 'care') && src.client_id ? { module: src.module, id: src.client_id, client_id: src.client_id, name: src.participant || '' }
        : (defaults.link || null),
    keepModule: src.id ? (src.module || null) : (defaults.module || null),
    attendees: src.attendees || defaults.attendees || [],
    organizer_account_id: src.organizer_account_id || defaults.organizer_account_id || '',
    invite_message: src.invite_message || defaults.invite_message || '',
    source_message_id: src.source_message_id || defaults.source_message_id || null,
  };
}
// The form as the API body, or { error }.
export function fromForm(f, scope = 'sc') {
  let start; let end;
  if (f.all_day) {
    start = parseYmd(f.date); const last = parseYmd(f.end_date || f.date);
    if (!start) return { error: 'Choose a date.' };
    if (!last || last < start) return { error: 'The last day is before the first day.' };
    end = addDays(last, 1);
  } else {
    start = atTime(f.date, f.start_time); end = atTime(f.end_date || f.date, f.end_time);
    if (!start) return { error: 'Choose a date and a start time.' };
    if (!end) return { error: 'Choose an end time.' };
    if (end <= start) return { error: 'The end must be after the start.' };
  }
  const k = kindOf(f.kind);
  const who = scope === 'email' ? f.link && f.link.name : f.coordination && f.coordination.name;
  const title = f.title.trim() || (who ? `${k.short} with ${who}` : k.label);
  // The SC calendar links an SC engagement; the Email calendar links SC, PM or a client (or keeps
  // an SC appointment on the SC calendar when it has no participant).
  const link = scope === 'email'
    ? (f.link && f.link.module === 'sc' ? { coordination_id: f.link.id, module: 'sc' }
      : f.link ? { coordination_id: null, module: f.link.module, client_id: f.link.client_id || f.link.id }
        : { coordination_id: null, module: f.keepModule === 'sc' ? 'sc' : null, client_id: null })
    : { coordination_id: f.coordination ? f.coordination.id : null };
  return {
    body: {
      title, kind: f.kind, ...link, coordinator_id: f.coordinator_id || null,
      attendees: f.attendees || [], organizer_account_id: f.organizer_account_id || null,
      invite_message: f.invite_message || '', ...(f.source_message_id ? { source_message_id: f.source_message_id } : {}),
      all_day: f.all_day, start_at: start.toISOString(), end_at: end.toISOString(), tz: TZ,
      repeat: f.repeat, repeat_until: f.repeat === 'none' ? null : (f.repeat_until || null),
      location: f.location, notes: f.notes, color: f.color || null, status: f.status,
      reminder_minutes: f.reminder === '' || f.reminder == null ? null : Number(f.reminder),
    },
    start, end,
  };
}

// Moving the start keeps the length; the end follows.
function withStart(f, date, time) {
  const s0 = f.all_day ? parseYmd(f.date) : atTime(f.date, f.start_time);
  const e0 = f.all_day ? parseYmd(f.end_date) : atTime(f.end_date, f.end_time);
  const s1 = f.all_day ? parseYmd(date) : atTime(date, time);
  if (!s0 || !e0 || !s1) return { ...f, date, start_time: time };
  const e1 = new Date(s1.getTime() + (e0 - s0));
  return { ...f, date, start_time: time, end_date: ymd(e1), end_time: f.all_day ? f.end_time : hm(e1) };
}

function useCoordinatorOptions(coordinators, staff) {
  return useMemo(() => {
    const seen = new Set();
    const out = [];
    [...(coordinators || []), ...(staff || [])].forEach((u) => {
      if (!u || !u.id || seen.has(String(u.id))) return;
      seen.add(String(u.id));
      out.push({ value: String(u.id), label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Staff member' });
    });
    return out;
  }, [coordinators, staff]);
}

// ── Quick create (the bubble a click opens) ─────────────────────────────────────────────────
export function QuickCreate({ draft, me, onChange, onSave, onMore, saving, scope = 'sc' }) {
  const f = draft.form;
  const set = (patch) => onChange({ ...draft, form: { ...f, ...patch } });
  const res = fromForm(f, scope);
  const when = res.start ? (f.all_day ? fmtLongDate(res.start) : `${fmtLongDate(res.start)} · ${fmtRange(res.start, res.end)}`) : '';
  return (
    <form className="p-5 space-y-4" onSubmit={(e) => { e.preventDefault(); onSave(); }}>
      <input data-autofocus value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Add title" aria-label="Title"
        className="w-full text-[20px] font-semibold text-slate-900 placeholder:text-slate-400 border-0 border-b-2 border-slate-200 rounded-none focus:border-indigo-600 !outline-none !ring-0 !shadow-none pb-1.5 bg-transparent" />
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Type">
        {KINDS.slice(0, 5).map((k) => (
          <button key={k.value} type="button" role="radio" aria-checked={f.kind === k.value} onClick={() => set({ kind: k.value })}
            className={clsx('inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12.5px] font-semibold transition-colors',
              f.kind === k.value ? 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200' : 'text-slate-600 hover:bg-slate-100')}>
            <k.icon size={13} aria-hidden />{k.short}
          </button>
        ))}
      </div>
      <p className="flex items-center gap-2.5 text-[13.5px] text-slate-700"><Clock size={16} className="text-slate-400" aria-hidden />{when}</p>
      <div className="flex items-center gap-2.5 text-[13.5px] text-slate-700">
        <Bell size={16} className="text-slate-400 flex-shrink-0" aria-hidden />
        <select value={f.reminder} onChange={(e) => set({ reminder: e.target.value })} aria-label="Reminder"
          className="bg-transparent text-[13.5px] text-slate-700 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 py-1 pr-1 -ml-1 cursor-pointer">
          {REMINDERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div>
        {scope === 'email' ? <LinkPicker value={f.link} onChange={(link) => set({ link })} /> : (
          <ParticipantPicker value={f.coordination} me={me} today={ymd(new Date())}
            onChange={(p) => set({ coordination: p ? { id: p.id, name: p.name } : null, coordinator_id: f.coordinator_id || (p && p.row && p.row.coordinator_id) || '' })} />
        )}
      </div>
      {(f.attendees || []).length > 0 && <p className="flex items-center gap-2.5 text-[13px] text-slate-600"><Users size={16} className="text-slate-400" aria-hidden />{f.attendees.length} {f.attendees.length === 1 ? 'person' : 'people'} invited</p>}
      {res.error && <p className="text-[12.5px] text-rose-600">{res.error}</p>}
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onMore}>More options</Button>
        <Button type="submit" loading={saving} disabled={!!res.error}>Save</Button>
      </div>
    </form>
  );
}

// ── The full editor (side sheet) ────────────────────────────────────────────────────────────
export function AppointmentSheet({ open, editing, form, onForm, onClose, onSave, onDelete, saving, me, coordinators, staff, occs, scope = 'sc' }) {
  const f = form;
  const set = (patch) => onForm({ ...f, ...patch });
  const coordOptions = useCoordinatorOptions(coordinators, staff);
  const [err, setErr] = useState(null);
  const res = f ? fromForm(f, scope) : {};
  const mailboxes = useMyMailboxes(!!open);
  // Inviting someone: the invites go from your own mailbox unless you choose another.
  const invited = !!(f && (f.attendees || []).length);
  useEffect(() => {
    if (open && invited && f && !f.organizer_account_id && mailboxes && mailboxes.length) onForm({ ...f, organizer_account_id: mailboxes[0].id });
  }, [open, invited, mailboxes, f, onForm]);
  const clashes = useMemo(() => {
    if (!f || !res.start || f.all_day || !f.coordinator_id) return [];
    return clashesFor({ key: editing ? editing.key : '__new__', coordinator_id: f.coordinator_id, start: res.start, end: res.end }, occs || []);
  }, [f, res.start, res.end, editing, occs]);
  if (!f) return null;
  const save = () => { if (res.error) { setErr(res.error); return; } setErr(null); onSave(); };

  return (
    <Sheet open={open} onClose={onClose} title={editing ? 'Edit appointment' : 'New appointment'} icon={CalendarDays} hue="indigo"
      sub={editing && editing.recurring ? 'This appointment repeats. You choose which ones change when you save.' : undefined}
      footer={<>
        {editing && <Button variant="ghost" className="mr-auto text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={onDelete}>Delete</Button>}
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={save}>Save</Button>
      </>}>
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <Field label="Title" hint="Leave it blank and the type and participant name it for you.">
          <Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder={fromForm({ ...f, title: '' }).body?.title || 'Add title'} autoFocus />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type">
            <Select value={f.kind} onChange={(v) => set({ kind: v })} options={KINDS.map((k) => ({ value: k.value, label: k.label }))} />
          </Field>
          <Field label={scope === 'email' ? 'Calendar' : 'Coordinator'}>
            <Select value={f.coordinator_id} onChange={(v) => set({ coordinator_id: v })} allowEmpty emptyLabel={scope === 'email' ? 'No one' : 'No coordinator'} options={coordOptions} placeholder={scope === 'email' ? 'No one' : 'No coordinator'} />
          </Field>
        </div>
        {scope === 'email' ? (
          <Field label="Linked to" hint="Optional. An SC participant, a PM participant or a client. It shows on their file.">
            <LinkPicker value={f.link} onChange={(link) => set({ link })} />
          </Field>
        ) : (
          <>
            <Field label="Participant" hint="Optional. Team meetings and travel can have none.">
              <ParticipantPicker value={f.coordination} me={me} today={ymd(new Date())}
                onChange={(p) => set({ coordination: p ? { id: p.id, name: p.name } : null, coordinator_id: f.coordinator_id || (p && p.row && p.row.coordinator_id) || '' })} />
            </Field>
            {f.coordination && <button type="button" className="-mt-3 text-[12.5px] font-semibold text-slate-500 hover:text-slate-800" onClick={() => set({ coordination: null })}>Remove participant</button>}
          </>
        )}

        <div className="rounded-2xl ring-1 ring-inset ring-slate-200/70 p-4 space-y-3">
          <Field label={<span className="inline-flex items-center gap-1.5"><Users size={13} aria-hidden />Invite people</span>}
            hint="They get a meeting invite by email with Accept and Decline. They see the title, time, place and your message; notes and the participant stay in Rostery.">
            <AttendeesField value={f.attendees || []} onChange={(attendees) => set({ attendees })} />
          </Field>
          {invited && (
            <>
              <OrganizerSelect value={f.organizer_account_id} mailboxes={mailboxes} onChange={(v) => set({ organizer_account_id: v || '' })} />
              <Field label="Message to the people invited" hint="Optional.">
                <Textarea rows={2} value={f.invite_message || ''} onChange={(e) => set({ invite_message: e.target.value })} />
              </Field>
            </>
          )}
        </div>

        <div className="rounded-2xl bg-slate-50/80 ring-1 ring-inset ring-slate-200/70 p-4 space-y-4">
          <Switch checked={f.all_day} onChange={(v) => set({ all_day: v })} label="All day" />
          <div className="grid grid-cols-2 gap-3">
            <Field label={f.all_day ? 'First day' : 'Date'} className="col-span-2">
              <Input type="date" value={f.date} onChange={(e) => onForm(withStart(f, e.target.value, f.start_time))} />
            </Field>
            {!f.all_day && (
              <>
                <Field label="Starts"><Input type="time" step={900} value={f.start_time} onChange={(e) => onForm(withStart(f, f.date, e.target.value))} /></Field>
                <Field label="Ends"><Input type="time" step={900} value={f.end_time} onChange={(e) => set({ end_time: e.target.value })} /></Field>
              </>
            )}
            {(f.all_day || f.end_date !== f.date) && (
              <Field label={f.all_day ? 'Last day' : 'Ends on'} className="col-span-2">
                <Input type="date" value={f.end_date} min={f.date} onChange={(e) => set({ end_date: e.target.value })} />
              </Field>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={<span className="inline-flex items-center gap-1.5"><Repeat size={13} aria-hidden />Repeat</span>}>
              <Select value={f.repeat} onChange={(v) => set({ repeat: v })} options={REPEATS} />
            </Field>
            {f.repeat !== 'none' && (
              <Field label="Repeat until" hint="Leave blank to keep repeating.">
                <Input type="date" value={f.repeat_until} min={f.date} onChange={(e) => set({ repeat_until: e.target.value })} />
              </Field>
            )}
          </div>
        </div>

        <Field label={<span className="inline-flex items-center gap-1.5"><Bell size={13} aria-hidden />Reminder</span>}
          hint={f.coordinator_id ? 'The coordinator gets an email, a notice in Rostery and one on the coordinator app.' : 'Choose a coordinator to send a reminder to.'}>
          <Select value={f.reminder} onChange={(v) => set({ reminder: v })} allowEmpty emptyLabel="No reminder" options={REMINDERS.filter((r) => r.value)} placeholder="No reminder" />
        </Field>

        {clashes.length > 0 && (
          <Banner tone="amber" icon={AlertTriangle} title={`This coordinator is already booked ${clashes.length === 1 ? 'then' : `${clashes.length} times then`}`}>
            {clashes.slice(0, 3).map((c) => `${c.title} (${fmtRange(c.start, c.end)})`).join(', ')}. You can still save it.
          </Banner>
        )}

        <Field label={<span className="inline-flex items-center gap-1.5"><MapPin size={13} aria-hidden />Location</span>}>
          <Input value={f.location} onChange={(e) => set({ location: e.target.value })} placeholder="Address, phone number or video link" />
        </Field>
        <Field label={<span className="inline-flex items-center gap-1.5"><AlignLeft size={13} aria-hidden />Notes</span>} hint="Seen by your team. Case notes still go in Notes & Billing.">
          <Textarea rows={4} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>

        <fieldset>
          <legend className="block text-[12.5px] font-semibold text-slate-700 mb-2">Colour</legend>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => set({ color: '' })} aria-pressed={!f.color}
              className={clsx('h-8 px-3 rounded-full text-[12px] font-semibold ring-1 ring-inset', !f.color ? 'bg-slate-900 text-white ring-slate-900' : 'text-slate-600 ring-slate-200 hover:bg-slate-50')}>
              Coordinator’s colour
            </button>
            {COLOR_KEYS.map((c) => (
              <button key={c} type="button" onClick={() => set({ color: c })} aria-pressed={f.color === c} aria-label={COLORS[c].label} title={COLORS[c].label}
                className={clsx('w-7 h-7 rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500',
                  f.color === c && 'ring-2 ring-offset-2 ring-slate-900')}
                style={{ background: COLORS[c].hex }} />
            ))}
          </div>
        </fieldset>

        {editing && (
          <Field label="Status">
            <Select value={f.status} onChange={(v) => set({ status: v })}
              options={[{ value: 'scheduled', label: 'Booked' }, { value: 'completed', label: 'Done' }, { value: 'cancelled', label: 'Cancelled' }]} />
          </Field>
        )}
        {err && <p className="text-[13px] text-rose-600" role="alert">{err}</p>}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Sheet>
  );
}
